/**
 * Public Digital Product Passport page.
 *
 * URL shape: `<host>/<EPCID-or-UID>`. NFC tags written by QDatDroid
 * point at `https://<dpp_host>/<UID>` so visiting the tag in a browser
 * renders this page. There is **no auth guard** — this route is the
 * public face of the cluster.
 *
 * The page mounts at the root level (not under `_layout`) so the
 * sidebar/auth-shell is bypassed. It fetches the assembled `DppPublic`
 * payload from the backend and dispatches each section in the template
 * order to a renderer below.
 */

import {
  Box,
  Container,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { QRCodeSVG } from "qrcode.react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  type DppPublic,
  type DppRenderedSection,
  type DppSectionTypeEnum,
  DppService,
} from "@/client"
import { Button } from "@/components/ui/button"
import { ColorModeButton } from "@/components/ui/color-mode"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { resolveLogoUrl } from "@/utils"

/** Hostname of the page (forced lowercase). SSR-safe via window guard. */
function getInstanceHost(): string {
  if (typeof window === "undefined") return ""
  return window.location.host.toLowerCase()
}

/** Full URL of the current page, untouched. */
function getCurrentUrl(): string {
  if (typeof window === "undefined") return ""
  return window.location.href
}

export const Route = createFileRoute("/$identifier")({
  component: PublicPassportPage,
})

// Static prefixes that are dashboard routes rather than DPP identifiers.
// Anything matching these falls through to a "not a passport" view so
// we don't accidentally render a bogus DPP for a typo'd dashboard URL.
const RESERVED_PREFIXES = new Set<string>([
  "login",
  "signup",
  "recover-password",
  "reset-password",
  "admin",
  "admin-audit",
  "admin-backup",
  "admin-branding",
  "admin-database",
  "admin-demo",
  "admin-dpp",
  "api-explorer",
  "api-tokens",
  "dashboard",
  "organisations",
  "readers",
  "tags",
  "tag-templates",
  "user-management",
  "user-permissions",
])

function PublicPassportPage() {
  const { identifier } = Route.useParams() as { identifier: string }
  const navigate = useNavigate()
  const sc = useSemanticColors()

  // Hand reserved paths back to the router so the SPA's normal
  // dashboard routes still work when reached via this catch-all.
  if (RESERVED_PREFIXES.has(identifier)) {
    navigate({ to: `/${identifier}` as never, replace: true })
    return null
  }

  const query = useQuery({
    queryKey: ["public-dpp", identifier],
    queryFn: () => DppService.resolvePublicPassport({ identifier }),
    retry: false,
    staleTime: 60_000,
  })

  if (query.isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={sc.pageBg}>
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  if (query.isError || !query.data) {
    return <NotFoundView identifier={identifier} />
  }

  // Template rules engine: if the resolver decided a rewrite rule
  // fires (geofence / elapsed-time / "beyond warranty"), hand the
  // visitor's browser to the operator-supplied URL instead of rendering
  // the passport. Use window.location.replace to keep the back button
  // honest — visitors pressing back land where they came from, not in
  // a redirect loop. ``redirect_url`` is added by the backend but the
  // generated client hasn't been regenerated yet; widen the type
  // locally rather than block this on a client regen.
  const redirectUrl = (query.data as { redirect_url?: string | null })
    .redirect_url
  if (redirectUrl) {
    if (typeof window !== "undefined") {
      window.location.replace(redirectUrl)
    }
    return (
      <Flex minH="100vh" align="center" justify="center" bg={sc.pageBg}>
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  if (query.data.status === "unbound") {
    return <UnboundView data={query.data} identifier={identifier} />
  }

  return <DppPage data={query.data} identifier={identifier} />
}

// ---------------------------------------------------------------------------
// Unbound view — tag exists but no template was bound to it.
// In demo mode: lets the visitor pick a template inline.
// Otherwise: directs them to log in as admin.
// ---------------------------------------------------------------------------
function UnboundView({
  data,
  identifier,
}: { data: DppPublic; identifier: string }) {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)

  const bindMutation = useMutation({
    mutationFn: (templateId: string) =>
      DppService.publicBindTagTemplate({
        identifier,
        requestBody: { template_id: templateId },
      }),
    onSuccess: (rendered) => {
      // Replace the cached entry with the freshly-bound payload so the
      // page transitions straight to the rendered DPP without a refetch.
      queryClient.setQueryData(["public-dpp", identifier], rendered)
    },
  })

  const branding = data.branding || {}
  const primaryColor = branding.primary_color || "#34a1dd"
  // No manufacturer data on the unbound view — the tag exists but no
  // template has been bound yet so we skip the footer CTA.
  const manufacturerName: string | undefined = undefined
  const manufacturerWebsite: string | undefined = undefined

  return (
    <Box
      minH="100vh"
      bg={sc.pageBg}
      pb={12}
      style={{ ["--dpp-primary" as string]: primaryColor }}
    >
      <DppHeader
        organisationName={data.organisation_name}
        logoUrl={resolveLogoUrl(branding.logo_url)}
        primaryColor={primaryColor}
      />
      <Container maxW="2xl" pt={6}>
        <Stack gap={5}>
          <Box
            bg={sc.cardBg}
            borderWidth="1px"
            borderColor={sc.border}
            borderRadius="lg"
            p={6}
          >
            <Heading size="md" color={sc.heading} mb={3}>
              {t("unbound.title")}
            </Heading>
            <Text color={sc.textMuted} mb={4}>
              {t("unbound.intro")}
            </Text>

            {data.tag_preview ? (
              <Box
                bg={sc.pageBg}
                borderWidth="1px"
                borderColor={sc.border}
                borderRadius="md"
                p={4}
                mb={4}
              >
                <SimpleGrid columns={2} gap={2}>
                  <Text color={sc.textMuted} fontSize="sm">
                    {t("unbound.preview.epcid")}
                  </Text>
                  <Text
                    color={sc.heading}
                    fontFamily="mono"
                    fontSize="sm"
                    fontWeight="semibold"
                  >
                    {data.tag_preview.epcid}
                  </Text>
                  {data.tag_preview.name ? (
                    <>
                      <Text color={sc.textMuted} fontSize="sm">
                        {t("unbound.preview.name")}
                      </Text>
                      <Text color={sc.heading} fontSize="sm">
                        {data.tag_preview.name}
                      </Text>
                    </>
                  ) : null}
                  {data.tag_preview.chip_type ? (
                    <>
                      <Text color={sc.textMuted} fontSize="sm">
                        {t("unbound.preview.chipType")}
                      </Text>
                      <Text color={sc.heading} fontSize="sm">
                        {data.tag_preview.chip_type}
                      </Text>
                    </>
                  ) : null}
                  {data.tag_preview.tag_type ? (
                    <>
                      <Text color={sc.textMuted} fontSize="sm">
                        {t("unbound.preview.tagType")}
                      </Text>
                      <Text color={sc.heading} fontSize="sm">
                        {data.tag_preview.tag_type}
                      </Text>
                    </>
                  ) : null}
                </SimpleGrid>
              </Box>
            ) : null}

            {data.is_demo && (data.available_templates?.length ?? 0) > 0 ? (
              <Box>
                <Text color={sc.heading} fontWeight="semibold" mb={2}>
                  {t("unbound.demo.pickTemplate")}
                </Text>
                <Stack gap={2} mb={4}>
                  {data.available_templates?.map((tpl) => (
                    <Button
                      key={tpl.id}
                      variant={
                        selectedTemplate === tpl.id ? "solid" : "outline"
                      }
                      onClick={() => setSelectedTemplate(tpl.id)}
                      bg={
                        selectedTemplate === tpl.id ? primaryColor : "transparent"
                      }
                      color={selectedTemplate === tpl.id ? "white" : sc.heading}
                      borderColor={sc.border}
                      justifyContent="space-between"
                    >
                      <Text>{tpl.name}</Text>
                      {tpl.is_default ? (
                        <Text fontSize="xs" opacity={0.7}>
                          {t("unbound.demo.defaultBadge")}
                        </Text>
                      ) : null}
                    </Button>
                  ))}
                </Stack>
                <Button
                  disabled={!selectedTemplate || bindMutation.isPending}
                  loading={bindMutation.isPending}
                  onClick={() => {
                    if (selectedTemplate) {
                      bindMutation.mutate(selectedTemplate)
                    }
                  }}
                  bg={primaryColor}
                  color="white"
                  width="full"
                >
                  {t("unbound.demo.bind")}
                </Button>
                {bindMutation.isError ? (
                  <Text color="red.400" fontSize="sm" mt={2}>
                    {t("unbound.demo.bindError")}
                  </Text>
                ) : null}
              </Box>
            ) : (
              <Box>
                <Text color={sc.textMuted} mb={3}>
                  {t("unbound.prod.adminRequired")}
                </Text>
                <Button
                  onClick={() =>
                    navigate({ to: "/login" as never })
                  }
                  bg={primaryColor}
                  color="white"
                  width="full"
                >
                  {t("unbound.prod.login")}
                </Button>
              </Box>
            )}
          </Box>
        </Stack>
      </Container>
      <DppFooter
        manufacturerName={manufacturerName}
        manufacturerWebsite={manufacturerWebsite}
      />
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Top-level page
// ---------------------------------------------------------------------------
function DppPage({
  data,
  identifier,
}: { data: DppPublic; identifier: string }) {
  const sc = useSemanticColors()
  const branding = data.branding || {}
  const primaryColor = branding.primary_color || "#34a1dd"

  // Pull manufacturer name + website from the always-on MANUFACTURER
  // section so the footer can render a "Visit manufacturer" button.
  const manufacturerSection = (data.sections ?? []).find(
    (s) => s.type === "MANUFACTURER",
  )
  const manufacturerName =
    (manufacturerSection?.data?.manufacturer_name as string | null | undefined) ??
    undefined
  const manufacturerWebsite =
    (manufacturerSection?.data?.manufacturer_website as
      | string
      | null
      | undefined) ?? undefined

  return (
    <Box
      minH="100vh"
      bg={sc.pageBg}
      pb={12}
      style={{
        // Lets the section components reference the active brand color
        // via `var(--dpp-primary)` for accents.
        ["--dpp-primary" as string]: primaryColor,
      }}
    >
      <DppHeader
        organisationName={data.organisation_name}
        logoUrl={resolveLogoUrl(branding.logo_url)}
        primaryColor={primaryColor}
      />
      <Container maxW="3xl" pt={6}>
        <Stack gap={5}>
          {(data.sections || []).map((section, idx) => (
            <SectionRenderer
              key={`${section.type}-${idx}`}
              section={section}
              identifier={identifier}
            />
          ))}
        </Stack>
      </Container>
      <DppFooter
        manufacturerName={manufacturerName}
        manufacturerWebsite={manufacturerWebsite}
      />
    </Box>
  )
}

function DppHeader({
  organisationName,
  logoUrl,
  primaryColor,
}: {
  organisationName: string
  logoUrl?: string | null
  primaryColor: string
}) {
  const sc = useSemanticColors()
  const instanceHost = getInstanceHost()
  const currentUrl = getCurrentUrl()
  return (
    <Box
      borderBottom="1px solid"
      borderColor={sc.border}
      bg={sc.cardBg}
      px={6}
      py={4}
    >
      <Container maxW="3xl" px={0}>
        <Flex align="center" gap={3}>
          {logoUrl ? (
            <Image src={logoUrl} alt={organisationName} h="32px" />
          ) : (
            <Box w="6px" h="32px" bg={primaryColor} borderRadius="full" />
          )}
          <Box flex="1" minW={0}>
            {/* Line 1 — instance host in lowercase, line 2 — full URL */}
            <Text
              color={sc.heading}
              fontFamily="mono"
              fontSize="sm"
              fontWeight="bold"
              lineHeight="1.2"
            >
              {instanceHost}
            </Text>
            <Text
              color={sc.textMuted}
              fontSize="xs"
              fontFamily="mono"
              lineHeight="1.2"
              truncate
            >
              {currentUrl}
            </Text>
            <Text fontSize="xs" color={sc.textMuted} mt={1}>
              {organisationName}
            </Text>
          </Box>
          {/* Top-right theme toggle, matching the dashboard navbar. */}
          <ColorModeButton />
        </Flex>
      </Container>
    </Box>
  )
}

function DppFooter({
  manufacturerName,
  manufacturerWebsite,
}: {
  manufacturerName?: string | null
  manufacturerWebsite?: string | null
}) {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  return (
    <Container maxW="3xl" pt={10}>
      {manufacturerWebsite ? (
        <Flex justify="center" mb={4}>
          <a
            href={manufacturerWebsite}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              variant="outline"
              bg="transparent"
              borderColor="var(--dpp-primary)"
              color="var(--dpp-primary)"
            >
              {t("footer.visitManufacturer", {
                name: manufacturerName ?? "manufacturer",
              })}
            </Button>
          </a>
        </Flex>
      ) : null}
      <Text fontSize="xs" color={sc.textMuted} textAlign="center">
        Digital Product Passport · powered by QDat.io
      </Text>
    </Container>
  )
}

// ---------------------------------------------------------------------------
// Section dispatcher
// ---------------------------------------------------------------------------
function SectionRenderer({
  section,
  identifier,
}: { section: DppRenderedSection; identifier: string }) {
  const sc = useSemanticColors()

  // All sections share the same card chrome — only the body changes.
  const card = (children: React.ReactNode) => (
    <Box
      p={5}
      bg={sc.cardBg}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={sc.border}
    >
      {section.title && (
        <Heading size="sm" color={sc.heading} mb={3}>
          {section.title}
        </Heading>
      )}
      {children}
    </Box>
  )

  switch (section.type as DppSectionTypeEnum) {
    case "HERO":
      return card(<HeroBody data={section.data} />)
    case "IDENTITY":
    case "SPECS":
    case "MATERIALS":
      return card(<KeyValueList data={section.data} />)
    case "LOCATION":
      return card(<LocationBody data={section.data} />)
    case "PROVENANCE":
      return card(<ProvenanceBody data={section.data} />)
    case "DOCUMENTS":
      return card(<DocumentsBody data={section.data} />)
    case "CONTACT":
      return card(<KeyValueList data={section.data} />)
    case "QR":
      return card(<QrBody identifier={identifier} />)
    case "PRODUCT":
      return card(<ProductBody data={section.data} />)
    case "MANUFACTURER":
      return card(<ContactBlockBody data={section.data} prefix="manufacturer" />)
    case "REPRESENTATIVE":
      return card(
        <ContactBlockBody data={section.data} prefix="representative" />,
      )
    case "SCAN_LOCATION":
      return card(<ScanLocationBody data={section.data} />)
    case "RFID":
      return card(<RfidBody data={section.data} />)
    case "SUSTAINABILITY":
      return card(<SustainabilityBody data={section.data} />)
    case "CUSTOM_HTML":
      return card(<CustomHtmlBody data={section.data} />)
    case "CUSTOM_JSON_LD":
      return <CustomJsonLdBody data={section.data} />
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// 2.0.0 mandatory section bodies
// ---------------------------------------------------------------------------
function ProductBody({ data }: { data?: Record<string, unknown> }) {
  const sc = useSemanticColors()
  const description = data?.product_description as string | null | undefined
  return (
    <Stack gap={3}>
      {description ? (
        <Text color={sc.text} fontSize="sm" whiteSpace="pre-wrap">
          {description}
        </Text>
      ) : null}
      <KeyValueList
        data={{
          serial_number: data?.serial_number,
          name: data?.name,
          category: data?.category,
          sku: data?.sku,
        }}
      />
    </Stack>
  )
}

function ContactBlockBody({
  data,
  prefix,
}: {
  data?: Record<string, unknown>
  prefix: "manufacturer" | "representative"
}) {
  const name = data?.[`${prefix}_name`] as string | null | undefined
  const address = data?.[`${prefix}_address`] as string | null | undefined
  const email = data?.[`${prefix}_email`] as string | null | undefined
  const phone = data?.[`${prefix}_phone`] as string | null | undefined
  const website = data?.[`${prefix}_website`] as string | null | undefined
  return (
    <KeyValueList
      data={{
        name,
        address,
        email,
        phone,
        website,
      }}
    />
  )
}

function ScanLocationBody({ data }: { data?: Record<string, unknown> }) {
  const lat = data?.last_lat as number | null | undefined
  const lon = data?.last_lon as number | null | undefined
  const alt = data?.last_altitude as number | null | undefined
  const seen = data?.last_seen_at as string | null | undefined
  const coords =
    lat != null && lon != null
      ? `${lat.toFixed(6)}, ${lon.toFixed(6)}`
      : undefined
  return (
    <KeyValueList
      data={{
        coordinates: coords,
        altitude_m: alt,
        last_seen_at: seen,
      }}
    />
  )
}

// Many UHF Gen2 readers report the full 64-byte TID memory bank padded
// with trailing zeros. The canonical TID is the first 96 bits / 24 hex
// chars (class identifier + MDID + TMN + XTID header + 48-bit serial);
// anything past that is bank padding or vendor extension that overwhelms
// the public page. Trim trailing 00 pairs to handle the (rarer) 64-bit
// chips cleanly, then cap at 24 hex chars.
function formatTid(raw: unknown): string | undefined {
  if (typeof raw !== "string" || !raw) return undefined
  const trimmed = raw.replace(/(?:00){2,}$/i, "")
  return trimmed.length > 24 ? trimmed.slice(0, 24) : trimmed
}

function RfidBody({ data }: { data?: Record<string, unknown> }) {
  return (
    <KeyValueList
      data={{
        epcid: data?.epcid,
        tag_type: data?.tag_type,
        chip_type: data?.chip_type,
        chip_vendor: data?.chip_vendor,
        tid: formatTid(data?.tid),
      }}
    />
  )
}

function SustainabilityBody({ data }: { data?: Record<string, unknown> }) {
  const sc = useSemanticColors()
  const co2e = data?.co2e_kg as number | null | undefined
  const methodology = data?.co2e_methodology as string | null | undefined
  const score = data?.repairability_score as number | null | undefined
  const notes = data?.repairability_notes as string | null | undefined
  return (
    <Stack gap={3}>
      <KeyValueList
        data={{
          co2e_kg: co2e != null ? `${co2e} kg CO₂e` : undefined,
          co2e_methodology: methodology,
          repairability_score:
            score != null ? `${score} / 10` : undefined,
        }}
      />
      {notes ? (
        <Text color={sc.text} fontSize="sm" whiteSpace="pre-wrap">
          {notes}
        </Text>
      ) : null}
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Section bodies
// ---------------------------------------------------------------------------
function HeroBody({ data }: { data?: Record<string, unknown> }) {
  const sc = useSemanticColors()
  const name = (data?.name as string | undefined) || "Asset"
  const category = data?.category as string | undefined
  const images = data?.images as string[] | undefined
  const heroImage = images?.[0]

  return (
    <Stack gap={3}>
      {heroImage && (
        <Image
          src={heroImage}
          alt={name}
          w="100%"
          maxH="320px"
          objectFit="cover"
          borderRadius="md"
        />
      )}
      <Heading size="lg" color={sc.heading}>
        {name}
      </Heading>
      {category && (
        <Text fontSize="sm" color={sc.textMuted}>
          {category}
        </Text>
      )}
    </Stack>
  )
}

function KeyValueList({ data }: { data?: Record<string, unknown> }) {
  const sc = useSemanticColors()
  const entries = Object.entries(data ?? {}).filter(
    ([k, v]) =>
      k !== "body_i18n" &&
      k !== "extra" &&
      v !== null &&
      v !== undefined &&
      v !== "",
  )

  if (entries.length === 0) {
    return (
      <Text color={sc.textMuted} fontSize="sm">
        —
      </Text>
    )
  }

  return (
    <Stack gap={2}>
      {entries.map(([key, value]) => (
        <Flex key={key} justify="space-between" gap={4}>
          <Text fontSize="sm" color={sc.textMuted} textTransform="capitalize">
            {key.replace(/_/g, " ")}
          </Text>
          <Text fontSize="sm" color={sc.heading} textAlign="right">
            {Array.isArray(value)
              ? value.length
                ? value.join(", ")
                : "—"
              : String(value)}
          </Text>
        </Flex>
      ))}
    </Stack>
  )
}

function LocationBody({ data }: { data?: Record<string, unknown> }) {
  // LOCATION just uses the same flat key/value rendering as IDENTITY,
  // but kept distinct so a future map embed can swap in without
  // touching the catch-all.
  return <KeyValueList data={data} />
}

function ProvenanceBody({ data }: { data?: Record<string, unknown> }) {
  const sc = useSemanticColors()
  const lastSeenAt = data?.last_seen_at as string | undefined
  const acqDate = data?.acquisition_date as string | undefined
  if (!lastSeenAt && !acqDate) {
    return (
      <Text color={sc.textMuted} fontSize="sm">
        —
      </Text>
    )
  }
  return (
    <Stack gap={3}>
      {acqDate && (
        <Flex justify="space-between">
          <Text fontSize="sm" color={sc.textMuted}>
            Acquired
          </Text>
          <Text fontSize="sm" color={sc.heading}>
            {acqDate}
          </Text>
        </Flex>
      )}
      {lastSeenAt && (
        <Flex justify="space-between">
          <Text fontSize="sm" color={sc.textMuted}>
            Last seen
          </Text>
          <Text fontSize="sm" color={sc.heading}>
            {new Date(lastSeenAt).toLocaleString()}
          </Text>
        </Flex>
      )}
    </Stack>
  )
}

function DocumentsBody({ data }: { data?: Record<string, unknown> }) {
  const sc = useSemanticColors()
  const images = (data?.images as string[] | undefined) ?? []
  if (images.length === 0) {
    return (
      <Text color={sc.textMuted} fontSize="sm">
        —
      </Text>
    )
  }
  return (
    <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
      {images.map((src) => (
        <Image
          key={src}
          src={src}
          alt="Document"
          w="100%"
          h="120px"
          objectFit="cover"
          borderRadius="md"
          border="1px solid"
          borderColor={sc.border}
        />
      ))}
    </SimpleGrid>
  )
}

function QrBody({ identifier }: { identifier: string }) {
  const sc = useSemanticColors()
  // Use the current URL so the QR points at exactly what the visitor is
  // currently viewing (works even on cluster previews / forked hosts).
  const url =
    typeof window !== "undefined" ? window.location.href : `/${identifier}`
  return (
    <Flex direction="column" align="center" gap={3}>
      <Box bg="white" p={3} borderRadius="md">
        <QRCodeSVG value={url} size={160} level="M" />
      </Box>
      <Text
        fontSize="xs"
        color={sc.textMuted}
        wordBreak="break-all"
        textAlign="center"
      >
        {url}
      </Text>
    </Flex>
  )
}

function CustomHtmlBody({ data }: { data?: Record<string, unknown> }) {
  const sc = useSemanticColors()
  const body = data?.body_i18n as Record<string, string> | undefined
  const html = body?.en || (body && Object.values(body)[0]) || ""
  if (!html) {
    return (
      <Text color={sc.textMuted} fontSize="sm">
        —
      </Text>
    )
  }
  // Server-trusted operator-authored HTML. The closed allowlist on the
  // backend prevents non-template-author input from reaching here, but
  // we still set the styling via `<Box>` so the host page CSS wins.
  return (
    <Box
      color={sc.heading}
      fontSize="sm"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function CustomJsonLdBody({ data }: { data?: Record<string, unknown> }) {
  const body = data?.body_i18n as Record<string, string> | undefined
  const raw = body?.en || (body && Object.values(body)[0]) || ""
  if (!raw) return null
  // Render a script tag so search engines pick this up for SEO. The
  // body is operator-authored on the template, not visitor input.
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: raw }}
    />
  )
}

// ---------------------------------------------------------------------------
// 404 view
// ---------------------------------------------------------------------------
function NotFoundView({ identifier }: { identifier: string }) {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  return (
    <Flex minH="100vh" align="center" justify="center" bg={sc.pageBg} px={4}>
      <Box
        maxW="md"
        textAlign="center"
        p={8}
        bg={sc.cardBg}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={sc.border}
      >
        <Heading size="md" color={sc.heading} mb={2}>
          {t("public.notFoundTitle", "No passport for this tag")}
        </Heading>
        <Text fontSize="sm" color={sc.textMuted} mb={4}>
          {t("public.notFoundBody", {
            defaultValue:
              "Identifier {{id}} is not registered, has been unpublished, or this cluster doesn't host the Digital Product Passport for it.",
            id: identifier,
          })}
        </Text>
      </Box>
    </Flex>
  )
}
